// ==========================================================================
// Squarebit Store - Copyright (c) 2026 Squarebit LLC. All rights reserved.
// Proprietary and confidential. Not for redistribution.
//
// NXT Editor changelog (store front end). The editor ships with the Squarebit
// Framework and is covered by the Squarebit Framework End User License
// Agreement: https://store.squarebitstudios.com/sbframework/eula
// ==========================================================================

import React from 'react';
import { ProductChangelog } from '../common/components/product-changelog.component';
import { NotFound } from '../common/components/404.component';
import { useHasFrameworkAccess } from './feature/use-sbframework-access';
import { NXT_CHANGELOG_PATH } from './feature/use-nxt-version';

/**
 * Passed as the shipped version so that nothing is filtered out.
 *
 * ProductChangelog hides entries newer than the version given, which is there
 * for a changelog written ahead of a release. This file is written when a
 * release goes out and documents only what has shipped, so there is nothing to
 * hide - and reading the current version out of the same file only to filter
 * that file by it would be a way to render it empty if it ever disagreed with
 * itself.
 */
const SHOW_EVERY_ENTRY = '999.0.0';

/**
 * The editor's changelog, reached from the version on its My Products card.
 *
 * Gated on the framework grant like the rest of the product: an account
 * without `sbframework` gets the ordinary not-found page rather than a 403, so
 * the route never confirms there is something behind it. The markdown is
 * served from /public and so is fetchable by anyone who guesses the filename -
 * the gate is about not ADVERTISING the product, not about keeping release
 * notes secret. The download is the thing that is actually protected, and that
 * check is server-side.
 *
 * No per-version downloads, unlike the framework's. The server holds one
 * editor archive and serves it as nxt_latest.zip; there is no older build to
 * offer, so a download link per version would promise something that is not
 * there. The current one is on the My Products card.
 */
const NxtChangelog: React.FC = () => {
  const { granted, isLoading } = useHasFrameworkAccess();

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-[80px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  // Rendered in place rather than redirected to, the same as the framework's:
  // a redirect rewrites the URL, so a holder who lands here while the session
  // is still resolving ends up parked on /not-found with no way back.
  if (!granted) return <NotFound />;

  return (
    <ProductChangelog
      productName="NXT Editor"
      version={SHOW_EVERY_ENTRY}
      mdPath={NXT_CHANGELOG_PATH}
      publicRepo={null}
      showDownloadLinks={false}
    />
  );
};

export default NxtChangelog;
